import {v3} from "../short3.js";
import Doms from "oc-commons/Doms.js";
import Math3 from "../Maths3.js";
import Screens from "../Screens.js";

const ConeStatics = {
    m1: new THREE.Matrix3(),
    v1: v3(),
    CmV: v3(),
    D: v3(),
};

/**
 * Ein Kegel
 */
export default class Cone {

    /**
     * Ein Kegel, dessen Spitze (top) im origin ist und der sich nach direction öffnet mit Winkel alpha.
     * direction must be normalized. height may be Number.POSITIVE_INFINITY
     */
    constructor (origin=v3(0,0,0), direction=v3(0,1,0), alpha=45*Math.PI/180, height=Number.POSITIVE_INFINITY) {
        this.origin = origin;
        this.direction = direction;
        this.alpha = alpha;
        this.height = height;
        this.resetCache();
    }

    resetCache() {
        this.bottomCenter_ = null;
        this.bottomHullPoint_ = null;
        this.center_ = null;
        this.sphere_ = null;
        this.line_ = null;
        this.tanAlpha = Math.tan(this.alpha);
        this.sinAlpha = Math.sin(this.alpha);
        this.cosAlpha = Math.cos(this.alpha);
    }

    set(origin, direction, alpha, height) {
        this.origin.copy( origin );
        this.direction.copy(direction);
        this.alpha = alpha;
        this.height = height;
        this.resetCache();
        return this;
    }

    setComponents ( ox, oy, oz, dx, dy, dz, alpha, height ) {
        this.origin.set( ox, oy, oz );
        this.direction.set( dx, dy, dz );
        this.radius = alpha;
        this.height = height;
        this.resetCache();
        return this;
    }

    setFromCameraForTouch(coords,touchRadiusInMM=2) {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera( coords, STAGE.camera );
        this.origin = raycaster.ray.origin;
        this.direction = raycaster.ray.direction;
        this.height = Number.POSITIVE_INFINITY;
        const vsf = Screens.visibleSizeFactor();
        const tpr = touchRadiusInMM * Doms.getPixelPerMM();
        this.alpha = Math.atan(tpr / vsf);
        this.resetCache();
        return this;
    }

    setFromTopCenterBottomCenterBottomHullPoint (topCenter,bottomCenter,bottomHullPoint) {
        this.origin.copy( topCenter );
        this.direction.subVectors( bottomCenter, topCenter );
        this.height = this.direction.length();
        this.direction.normalize();
        this.alpha = v3().copy(bottomHullPoint).sub(topCenter).angleTo(this.direction);
        this.resetCache();
        return this;
    }

    clone () {
        return new Cone(this.origin.clone(),this.direction.clone(),this.alpha,this.height);
    }

    copy ( cone ) {
        return this.set(cone.origin,cone.direction,cone.alpha,cone.height);
    }

    topCenter() {
        return this.origin;
    }

    bottomCenter() {
        if (!this.bottomCenter_) {
            this.bottomCenter_ = this.direction.clone().multiplyScalar(this.height).add(this.origin);
        }
        return this.bottomCenter_;
    }

    bottomHullPoint() {
        if (!this.bottomHullPoint_) {
            this.bottomHullPoint_ = Math3.getAnyOrthonormalVector(this.direction).multiplyScalar(this.height*Math.tan(this.alpha)).add(this.origin);
        }
        return this.bottomHullPoint_;
    }

    center() {
        if (!this.center_) {
            this.center_ = this.direction.clone().multiplyScalar(this.height/2).add(this.origin);
        }
        return this.center_;
    }

    getBoundingSphere() {
        if (!this.sphere_) {
            this.sphere_ = new THREE.Sphere(this.center(), this.center().distanceTo(this.bottomHullPoint()));
        }
        return this.sphere_;
    }

    getLine() {
        if (!this.line_) {
            this.line_ = new THREE.Line3(this.topCenter(), this.bottomCenter());
        }
        return this.line_;
    }

    setAlpha(alpha) {
        this.alpha = alpha;
        this.resetCache();
        return this;
    }

    setHeight(height) {
        this.height = height;
        this.resetCache();
        return this;
    }

    setDirection(direction) {
        this.direction = direction;
        this.resetCache();
        return this;
    }

    setOrigin(origin) {
        this.origin = origin;
        this.resetCache();
        return this;
    }

    radius(atLength) {
        return this.tanAlpha*atLength;
    }

    radiusBottom() {
        return this.radius(this.height);
    }

    applyMatrix4 ( matrix ) {
        this.direction.add( this.origin ).applyMatrix4( matrix );
        this.origin.applyMatrix4( matrix );
        this.direction.sub( this.origin );
        this.direction.normalize();
        const scale = matrix.getMaxScaleOnAxis();
        this.height = this.height * scale;
        this.resetCache();
        return this;
    }

    translate ( offset ) {
        this.origin.add( offset);
        this.resetCache();
        return this;
    }

    equals ( cone ) {
        return this.alpha==cone.alpha && this.height==cone.height && this.origin.equals( cone.origin ) && this.direction.equals( cone.direction );
    }

    isIntersectionSphere ( sphere ) {
        const invSin = 1/this.sinAlpha;
        const cosSqr = Math.pow(this.cosAlpha,2);
        const CmV = ConeStatics.CmV.subVectors(sphere.center,this.origin);
        const D = ConeStatics.D.copy(this.direction).multiplyScalar(sphere.radius*invSin).add(CmV);
        let DSqrLen = D.lengthSq();
        let e = D.dot(this.direction);
        if (e > 0 && e*e >= DSqrLen*cosSqr) {
            const sinSqr = Math.pow(this.sinAlpha,2);
            DSqrLen = CmV.lengthSq();
            e = -CmV.dot(this.direction);
            if (e > 0 && e*e >= DSqrLen*sinSqr) {
                const rSqr = sphere.radius*sphere.radius;
                return DSqrLen <= rSqr;
            }
            return true;
        }
        return false;
    }

    //distanceToPoint ( point ) {
    //}
    //
    //distanceToSphere ( sphere ) {
    //}
    //
    //isIntersectionLine ( line ) {
    //}
    //
    //intersectLine () {
    //}
}
